import React from "react";
import { Link } from "react-router-dom";
import { Zap, Phone, ArrowRight, Check, Leaf, DollarSign } from "lucide-react";
import { PHONE, PHONE_TEL } from "../data/content";

const FLEET_IMAGE_SRC = "/images/fleet/geely-ex5.jpg";

const BENEFITS = [
  { label: "$0 to get a technician to your door", icon: DollarSign },
  { label: "Transparent hourly pricing — you know the rates before any work starts", icon: Check },
  { label: "No fix · No fee on remedial repairs", icon: Check },
  { label: "A fully-electric mobile IT fleet", icon: Leaf },
];

export default function FleetSection() {
  return (
    <section className="py-12 md:py-20 relative overflow-hidden" data-testid="fleet-section">
      <div
        className="absolute top-1/2 -translate-y-1/2 left-0 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: "var(--te-primary)" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">

          {/* Image */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div
              className="relative aspect-[4/3] border border-white/10 overflow-hidden"
              style={{ background: "var(--te-paper)" }}
              data-testid="fleet-image-wrap"
            >
              {/* Placeholder content — renders behind, revealed only if img fails to load */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 pointer-events-none">
                <div
                  className="w-20 h-20 mb-6 flex items-center justify-center"
                  style={{ background: "var(--te-primary)" }}
                >
                  <Zap size={36} color="#fff" />
                </div>
                <div className="te-overline mb-2">/ Our fleet</div>
                <div className="text-2xl font-bold tracking-tight text-zinc-200 mb-1">
                  Black Geely EX5
                </div>
                <div className="text-sm text-zinc-500">
                  Adelaide's electric mobile IT fleet
                </div>
              </div>

              {/* Real photo sits on top, fully covers the placeholder when loaded */}
              <img
                src={FLEET_IMAGE_SRC}
                alt="Tech Emergency black Geely EX5 — Adelaide's electric mobile IT fleet"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover z-[1]"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />

              {/* Corner badge sits above everything */}
              <div
                className="absolute top-4 left-4 px-3 py-1.5 flex items-center gap-1.5 z-10"
                style={{ background: "rgba(5,5,5,0.85)", backdropFilter: "blur(6px)" }}
              >
                <Zap size={12} fill="var(--te-primary)" stroke="var(--te-primary)" />
                <span className="te-overline text-[10px] text-white">100% ELECTRIC FLEET</span>
              </div>
            </div>
          </div>

          {/* Copy */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10 mb-5">
              <DollarSign size={12} style={{ color: "var(--te-primary)" }} />
              <span className="te-overline text-[10px]">Why we can charge less</span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight mb-6">
              Zero callout<br />
              <span style={{ color: "var(--te-primary)" }}>fee. Ever.</span>
            </h2>

            <p className="text-lg text-zinc-300 leading-relaxed mb-6">
              Most Adelaide computer repair companies charge a callout fee just to show up at
              your door — before they've even looked at your computer.
            </p>
            <p className="text-zinc-300 leading-relaxed mb-8">
              <strong className="text-white">We don't charge a cent to come to you.</strong>{" "}
              Our fleet is fully electric, our running costs are tiny, and we pass the saving
              straight back. You only pay when we've actually fixed your problem.
            </p>

            <ul className="space-y-3 mb-8" data-testid="fleet-benefits">
              {BENEFITS.map((b) => (
                <li key={b.label} className="flex items-start gap-3 text-zinc-200">
                  <span
                    className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: "var(--te-primary)" }}
                  >
                    <b.icon size={13} color="#fff" />
                  </span>
                  <span className="text-sm sm:text-base leading-relaxed">{b.label}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={PHONE_TEL}
                data-testid="fleet-call-btn"
                className="te-btn te-btn-primary"
              >
                <Phone size={16} /> {PHONE}
              </a>
              <Link
                to="/about"
                data-testid="fleet-learn-more"
                className="te-btn te-btn-ghost"
              >
                Why electric? <ArrowRight size={14} />
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
